import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  TextField,
  Chip,
  Box,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  Checkbox,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Photo as PhotoIcon,
  CallSplit as SplitIcon,
  MergeType as MergeIcon,
  Save as SaveIcon,
  MoreVert as MoreVertIcon,
  ContentCut as CutIcon,
} from '@mui/icons-material';
import { useAlbum } from '../../contexts/AlbumContext';
import { useApi } from '../../hooks/useApi';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const EditStep = () => {
  const { groups, projectId, nextStep, mergeGroups, splitGroup, saveAlbum } = useAlbum();
  const { updateGroup } = useApi();

  // 그룹 합치기
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // 그룹 분리
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitGroupId, setSplitGroupId] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);

  // 메뉴
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuGroupId, setMenuGroupId] = useState(null);

  const handleLocationChange = (groupId, value) => {
    updateGroup(groupId, 'location', value);
  };

  const handleActivityChange = (groupId, value) => {
    updateGroup(groupId, 'activity', value);
  };

  const handlePlaceSelect = (groupId, placeName) => {
    updateGroup(groupId, 'location', placeName);
  };

  // 저장 처리
  const handleSave = () => {
    saveAlbum();
  };

  // 합치기 모드 토글
  const handleMergeMode = () => {
    if (mergeMode && selectedGroups.length === 2) {
      mergeGroups(selectedGroups[0], selectedGroups[1]);
      setSelectedGroups([]);
    }
    setMergeMode(!mergeMode);
    setSelectedGroups([]);
  };

  // 그룹 선택 (합치기용)
  const handleGroupSelect = (groupId) => {
    if (!mergeMode) return;

    if (selectedGroups.includes(groupId)) {
      setSelectedGroups(selectedGroups.filter(id => id !== groupId));
    } else if (selectedGroups.length < 2) {
      setSelectedGroups([...selectedGroups, groupId]);
    }
  };

  // 분리 다이얼로그 열기
  const openSplitDialog = (groupId) => {
    setSplitGroupId(groupId);
    setSelectedPhotoIndex(null);
    setSplitDialogOpen(true);
    setMenuAnchor(null);
  };

  // 분리 실행
  const handleSplit = () => {
    if (splitGroupId && selectedPhotoIndex !== null) {
      splitGroup(splitGroupId, selectedPhotoIndex);
      setSplitDialogOpen(false);
      setSplitGroupId(null);
      setSelectedPhotoIndex(null);
    }
  };

  // 메뉴 열기
  const handleMenuOpen = (event, groupId) => {
    setMenuAnchor(event.currentTarget);
    setMenuGroupId(groupId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuGroupId(null);
  };

  const currentSplitGroup = groups.find(g => g.group_id === splitGroupId);

  return (
    <Stack spacing={3}>
      {/* 헤더 카드 */}
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 2 }}>
          <EditIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h2" gutterBottom>
            그룹 확인 및 편집
          </Typography>
          <Typography variant="body2" color="text.secondary">
            자동 식별된 장소를 확인하고 필요시 수정하세요.
          </Typography>
        </CardContent>
      </Card>

      {/* 액션 버튼들 */}
      <Stack direction="row" spacing={1} justifyContent="center">
        <Button
          variant={mergeMode ? 'contained' : 'outlined'}
          size="small"
          startIcon={<MergeIcon />}
          onClick={handleMergeMode}
          color={mergeMode ? (selectedGroups.length === 2 ? 'success' : 'primary') : 'inherit'}
        >
          {mergeMode
            ? selectedGroups.length === 2
              ? '합치기 실행'
              : `그룹 선택 (${selectedGroups.length}/2)`
            : '그룹 합치기'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleSave}
        >
          저장
        </Button>
      </Stack>

      {/* 그룹 카드들 */}
      {groups.map((group) => (
        <Card
          key={group.group_id}
          sx={{
            border: mergeMode && selectedGroups.includes(group.group_id)
              ? '2px solid'
              : '1px solid',
            borderColor: mergeMode && selectedGroups.includes(group.group_id)
              ? 'primary.main'
              : 'divider',
            cursor: mergeMode ? 'pointer' : 'default',
          }}
          onClick={() => handleGroupSelect(group.group_id)}
        >
          <CardContent>
            {/* 그룹 헤더 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {mergeMode && (
                  <Checkbox
                    checked={selectedGroups.includes(group.group_id)}
                    size="small"
                  />
                )}
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary"
                >
                  그룹 {group.group_id}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<PhotoIcon sx={{ fontSize: 16 }} />}
                  label={`${group.num_photos}장`}
                  size="small"
                  variant="outlined"
                />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, group.group_id);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* 썸네일 */}
            {group.photos && group.photos.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <ImageList
                  sx={{
                    width: '100%',
                    height: 80,
                    m: 0,
                  }}
                  cols={Math.min(group.photos.length, 5)}
                  rowHeight={70}
                  gap={4}
                >
                  {group.photos.slice(0, 5).map((photo, idx) => (
                    <ImageListItem key={idx}>
                      <img
                        src={`${API_URL}/thumbnail/${projectId}/${photo.filename}`}
                        alt={`사진 ${idx + 1}`}
                        loading="lazy"
                        style={{
                          objectFit: 'cover',
                          borderRadius: 8,
                          width: '100%',
                          height: '100%',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
                {group.photos.length > 5 && (
                  <Typography variant="caption" color="text.secondary">
                    +{group.photos.length - 5}장 더
                  </Typography>
                )}
              </Box>
            )}

            <Stack spacing={2}>
              {/* 장소명 */}
              <TextField
                label="장소명"
                value={group.location || ''}
                onChange={(e) => handleLocationChange(group.group_id, e.target.value)}
                placeholder="장소명 입력"
                size="small"
                fullWidth
                onClick={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <LocationOnIcon
                      sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }}
                    />
                  ),
                }}
              />

              {/* 추천 장소 */}
              {group.nearby_places && group.nearby_places.length > 0 && (
                <Box onClick={(e) => e.stopPropagation()}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    추천 장소:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {group.nearby_places.slice(0, 3).map((place, idx) => (
                      <Chip
                        key={idx}
                        label={place.name}
                        size="small"
                        onClick={() => handlePlaceSelect(group.group_id, place.name)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'primary.light',
                            color: 'primary.contrastText',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* 활동/분위기 */}
              <TextField
                label="활동/분위기"
                value={group.activity || ''}
                onChange={(e) => handleActivityChange(group.group_id, e.target.value)}
                placeholder="예: 야경 감상, 식사, 관광"
                size="small"
                fullWidth
                onClick={(e) => e.stopPropagation()}
              />

              <Divider />

              {/* 시간 정보 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.secondary',
                }}
              >
                <ScheduleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption">
                  {group.first_datetime} ~ {group.last_datetime}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}

      {/* 다음 버튼 */}
      <Button
        variant="contained"
        size="large"
        onClick={nextStep}
        fullWidth
        sx={{ py: 1.5 }}
      >
        다음 단계로
      </Button>

      {/* 그룹 메뉴 */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => openSplitDialog(menuGroupId)}>
          <SplitIcon sx={{ mr: 1 }} fontSize="small" />
          그룹 분리
        </MenuItem>
      </Menu>

      {/* 분리 다이얼로그 */}
      <Dialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CutIcon />
            그룹 분리
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            분리할 지점의 사진을 선택하세요. 선택한 사진부터 새 그룹이 됩니다.
          </Typography>
          {currentSplitGroup?.photos && (
            <ImageList cols={4} gap={8}>
              {currentSplitGroup.photos.map((photo, idx) => (
                <ImageListItem
                  key={idx}
                  onClick={() => idx > 0 && setSelectedPhotoIndex(idx)}
                  sx={{
                    cursor: idx > 0 ? 'pointer' : 'not-allowed',
                    opacity: idx === 0 ? 0.5 : 1,
                    border: selectedPhotoIndex === idx ? '3px solid' : '1px solid',
                    borderColor: selectedPhotoIndex === idx ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={`${API_URL}/thumbnail/${projectId}/${photo.filename}`}
                    alt={`사진 ${idx + 1}`}
                    loading="lazy"
                    style={{ objectFit: 'cover', height: 80 }}
                  />
                  {selectedPhotoIndex === idx && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontSize: '0.7rem',
                        textAlign: 'center',
                        py: 0.25,
                      }}
                    >
                      여기서 분리
                    </Box>
                  )}
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitDialogOpen(false)}>취소</Button>
          <Button
            onClick={handleSplit}
            variant="contained"
            disabled={selectedPhotoIndex === null}
          >
            분리
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EditStep;
